(function () {
  const calculatorDefaults = {
    modulePower: 440,
    bifacialGround: "light"
  };

  const bifacialBonuses = {
    light: 0.3,
    dark: 0.1
  };

  const calculationFactors = {
    season: {
      spring: 0.82,
      summer: 1,
      autumn: 0.65,
      winter: 0.38
    }
  };

  const directionAnchors = [
    { degree: 0, key: "north", factor: 0.35 },
    { degree: 90, key: "east", factor: 0.78 },
    { degree: 180, key: "south", factor: 1 },
    { degree: 270, key: "west", factor: 0.78 },
    { degree: 360, key: "north", factor: 0.35 }
  ];

  const sunHeightRatios = {
    spring: 0.63,
    summer: 1,
    autumn: 0.63,
    winter: 0.25
  };

  const sunPositionBounds = {
    desktop: {
      highest: -44,
      lowest: 54
    },
    mobile: {
      highest: -32,
      lowest: 56
    }
  };

  function formatFactor(factor) {
    return `${Math.round(factor * 100)}%`;
  }

  function getIdealAngle(season) {
    return season === "winter" ? 40 : 30;
  }

  function calculateAngleFactor(angle, season) {
    const numericAngle = Number(angle);
    const distanceFromIdeal = Math.abs(numericAngle - getIdealAngle(season));
    const factor = 1 - distanceFromIdeal * 0.006;

    return Math.max(0.78, factor);
  }

  function normalizeDirectionDegree(degree) {
    const numericDegree = Number(degree) || 0;

    if (numericDegree >= 365) {
      return 0;
    }

    return ((numericDegree % 360) + 360) % 360;
  }

  function calculateDirectionFactor(degree) {
    const normalizedDegree = normalizeDirectionDegree(degree);
    const nextAnchorIndex = directionAnchors.findIndex((anchor) => anchor.degree >= normalizedDegree);
    const nextAnchor = directionAnchors[nextAnchorIndex] || directionAnchors[directionAnchors.length - 1];
    const previousAnchor = directionAnchors[Math.max(0, nextAnchorIndex - 1)];
    const span = nextAnchor.degree - previousAnchor.degree || 1;
    const progress = (normalizedDegree - previousAnchor.degree) / span;

    return previousAnchor.factor + (nextAnchor.factor - previousAnchor.factor) * progress;
  }

  function getNearestDirectionKey(degree) {
    const normalizedDegree = normalizeDirectionDegree(degree);
    const cardinalIndex = Math.round(normalizedDegree / 90) % 4;

    return ["north", "east", "south", "west"][cardinalIndex];
  }

  function getNearestDirectionLabel(degree) {
    const directionLabels = {
      north: "Nord",
      east: "Ost",
      south: "Süd",
      west: "West"
    };

    return directionLabels[getNearestDirectionKey(degree)];
  }

  function calculateSunTop(season, viewport) {
    const ratio = sunHeightRatios[season] || sunHeightRatios.summer;
    const bounds = sunPositionBounds[viewport] || sunPositionBounds.desktop;
    const winterRatio = sunHeightRatios.winter;
    const usableProgress = (ratio - winterRatio) / (sunHeightRatios.summer - winterRatio);

    return Math.round(bounds.lowest - usableProgress * (bounds.lowest - bounds.highest));
  }

  function calculatePower(settings) {
    const angleFactor = calculateAngleFactor(settings.angle, settings.season);
    const directionFactor = calculateDirectionFactor(settings.direction);
    const seasonFactor = calculationFactors.season[settings.season] || 1;
    const bifacialFactor = 1 + (bifacialBonuses[settings.bifacialGround] || 0);
    const modulePower = Number(settings.modulePower) || calculatorDefaults.modulePower;

    return Math.round(modulePower * angleFactor * directionFactor * seasonFactor * bifacialFactor);
  }

  function initCalculator(root, index) {
    const angleInput = root.querySelector("[data-smc-angle]");
    const directionInput = root.querySelector("[data-smc-direction]");
    const seasonInputs = root.querySelectorAll("[data-smc-season] input");
    const modulePowerInputs = root.querySelectorAll("[data-smc-module-power] input");
    const bifacialInputs = root.querySelectorAll("[data-smc-bifacial] input");
    const angleOutput = root.querySelector("[data-smc-angle-output]");
    const directionOutput = root.querySelector("[data-smc-direction-output]");
    const output = root.querySelector("[data-smc-output]");
    const directionFactorOutput = root.querySelector("[data-smc-direction-factor]");
    const seasonFactorOutput = root.querySelector("[data-smc-season-factor]");
    const angleFactorOutput = root.querySelector("[data-smc-angle-factor]");
    const bifacialFactorOutput = root.querySelector("[data-smc-bifacial-factor]");
    const module = root.querySelector("[data-smc-module]");
    const moduleTurntable = root.querySelector("[data-smc-module-turntable]");
    const sun = root.querySelector(".smc-sun");
    const compassMarkers = root.querySelectorAll("[data-smc-compass-direction]");
    const seasonOptions = root.querySelectorAll(".smc-season-option");
    const modulePowerName = `smc-module-power-${index}`;
    const seasonName = `smc-season-${index}`;
    const bifacialName = `smc-bifacial-${index}`;

    modulePowerInputs.forEach((input) => {
      input.name = modulePowerName;
    });

    seasonInputs.forEach((input) => {
      input.name = seasonName;
    });

    bifacialInputs.forEach((input) => {
      input.name = bifacialName;
    });

    function update() {
      const selectedModulePower = root.querySelector("[data-smc-module-power] input:checked");
      const selectedSeason = root.querySelector("[data-smc-season] input:checked");
      const selectedBifacial = root.querySelector("[data-smc-bifacial] input:checked");
      const settings = {
        angle: angleInput.value,
        direction: directionInput.value,
        season: selectedSeason ? selectedSeason.value : "summer",
        modulePower: selectedModulePower ? selectedModulePower.value : calculatorDefaults.modulePower,
        bifacialGround: selectedBifacial ? selectedBifacial.value : calculatorDefaults.bifacialGround
      };

      angleOutput.textContent = `${settings.angle} Grad`;
      directionOutput.textContent = `${settings.direction} Grad (${getNearestDirectionLabel(settings.direction)})`;
      output.textContent = calculatePower(settings);

      if (directionFactorOutput) {
        directionFactorOutput.textContent = `${formatFactor(calculateDirectionFactor(settings.direction))} ${getNearestDirectionLabel(settings.direction)}`;
      }

      if (seasonFactorOutput) {
        seasonFactorOutput.textContent = formatFactor(calculationFactors.season[settings.season] || 1);
      }

      if (angleFactorOutput) {
        angleFactorOutput.textContent = formatFactor(calculateAngleFactor(settings.angle, settings.season));
      }

      if (bifacialFactorOutput) {
        bifacialFactorOutput.textContent = `+${Math.round((bifacialBonuses[settings.bifacialGround] || 0) * 100)}%`;
      }

      if (sun) {
        sun.style.setProperty("--smc-sun-top", `${calculateSunTop(settings.season, "desktop")}px`);
        sun.style.setProperty("--smc-sun-top-mobile", `${calculateSunTop(settings.season, "mobile")}px`);
      }

      seasonOptions.forEach((option) => {
        const input = option.querySelector("input");
        option.classList.toggle("is-active", Boolean(input && input.checked));
      });

      module.style.setProperty("--smc-module-angle", `${Number(settings.angle) * -1}deg`);
      moduleTurntable.style.setProperty("--smc-module-direction", `${normalizeDirectionDegree(settings.direction) - 180}deg`);
      compassMarkers.forEach((marker) => {
        marker.classList.toggle("is-active", marker.dataset.smcCompassDirection === getNearestDirectionKey(settings.direction));
      });
    }

    angleInput.addEventListener("input", update);
    directionInput.addEventListener("input", update);
    seasonInputs.forEach((input) => input.addEventListener("change", update));
    modulePowerInputs.forEach((input) => input.addEventListener("change", update));
    bifacialInputs.forEach((input) => input.addEventListener("change", update));

    update();
  }

  document.querySelectorAll("[data-smc-calculator]").forEach(initCalculator);
})();
